import React, { useState, useEffect, useRef, useCallback, Suspense, lazy } from 'react';
import { ChevronLeft, ChevronRight, ArrowRight } from 'lucide-react';
import { useInViewAnimation } from './hooks/useInViewAnimation';
import { useLiveStatus } from './hooks/useLiveStatus';
import { shippedProjects } from './data/projects';
import { Button } from './components/Button';
import { ContactModal } from './components/ContactModal';
import { BlogPage } from './components/BlogPage';
import { SiteNav } from './components/SiteNav';
import { HowIWorkSection } from './components/HowIWork';
import { WorkPage } from './components/WorkPage';
import { CaseStudiesPage } from './components/CaseStudiesPage';
import { AutomationsPage } from './components/AutomationsPage';
import { FAQ } from './components/FAQ';
// The case studies carry ~10 kB gzipped of prose that only /case-studies/:slug needs.
// Splitting them out keeps the homepage bundle where it was.
const CaseStudyPage = lazy(() =>
  import('./components/CaseStudyPage').then((m) => ({ default: m.CaseStudyPage }))
);
import { SelectedWorkSection, OpenSourceSection } from './components/Showcase';
import './App.css';

// ============ DATA ============

const testimonials = [
  {
    quote: "Partnering with Brandon has been a great experience. He brings a rare combination of technical depth and business understanding that makes collaboration effortless. If you need someone who gets both the product and the vision, he's your guy.",
    author: "Pedro",
    role: "Co-Founder, JP Trading Capital",
    avatar: "/images/testimonials/pedro.jpg",
  },
  {
    quote: "Brandon is one of those rare professionals who genuinely invests in your success. When we were navigating enterprise blockchain at ALTA, he didn't just execute — he brought strategic thinking that shaped our long-term direction. If you need someone who bridges deep technical implementation with real business outcomes, Brandon is that person.",
    author: "Yaroslav Ivanov",
    role: "CVO, ALTA Blockchain Lab",
    avatar: "/images/testimonials/yaroslav.jpg",
  },
];

// ============ COMPONENTS ============

// Hero Section
const HeroSection = ({ onOpenContact, onScrollToWork }) => {
  const { ref, isInView } = useInViewAnimation();
  // "Ten products shipped and live" was true when it was typed and had no way
  // of staying true. The count comes from the daily check now; without one, the
  // line claims only what a count of links can support.
  const { liveCount } = useLiveStatus();
  const total = shippedProjects.length;

  return (
    <section
      ref={ref}
      className="max-w-[680px] mx-auto text-center px-6 pt-16 pb-4 md:pt-24"
      data-testid="hero-section"
    >
      {/* Eyebrow */}
      <p
        className={`font-mono text-xs md:text-sm uppercase tracking-[0.2em] text-[#273C46]/70 mb-6 ${isInView ? 'animate-fade-in-up' : 'opacity-0'}`}
        style={{ animationDelay: '0.1s' }}
        data-testid="hero-tagline"
      >
        Brandon Crenshaw · Chicago
      </p>

      {/* Headline — the page's h1. The handle used to hold this slot, which
          told search engines and screen readers nothing about the offer. */}
      <h1
        className={`text-4xl md:text-5xl lg:text-6xl leading-[1.08] text-[#051A24] mb-6 text-balance ${isInView ? 'animate-fade-in-up' : 'opacity-0'}`}
        style={{ animationDelay: '0.2s' }}
        data-testid="hero-heading"
      >
        I build AI products that{' '}
        <span style={{ fontFamily: "'Playfair Display', serif" }} className="italic">
          make money
        </span>
        , not demos.
      </h1>

      {/* Description */}
      <div
        className={`text-[#273C46] text-base md:text-lg leading-relaxed mb-9 ${isInView ? 'animate-fade-in-up' : 'opacity-0'}`}
        style={{ animationDelay: '0.3s' }}
        data-testid="hero-description"
      >
        <p>
          I build the product, the agents behind it, and the plan to sell it.{' '}
          {liveCount === null
            ? `${total} products shipped, every one linked on the work page.`
            : liveCount >= total
              ? `${total} products shipped, all ${total} up at this morning's check.`
              : `${total} products shipped, ${liveCount} up at this morning's check.`}
        </p>
      </div>

      {/* Buttons */}
      <div
        className={`flex flex-col sm:flex-row gap-3 justify-center ${isInView ? 'animate-fade-in-up' : 'opacity-0'}`}
        style={{ animationDelay: '0.4s' }}
      >
        <Button
          variant="primary"
          onClick={onOpenContact}
          data-testid="hero-start-chat-btn"
        >
          Book a 20 minute call
        </Button>
        <Button
          variant="secondary"
          onClick={onScrollToWork}
          data-testid="hero-see-work-btn"
        >
          See the work
        </Button>
      </div>
    </section>
  );
};

// Positioning statement + portrait.
// This used to be a pull quote attributed to Brandon praising his own mind,
// sitting in the visual slot reserved for client testimonials. Same substance,
// stated plainly, so the real testimonials below carry the praise.
const PositioningSection = () => {
  const { ref, isInView } = useInViewAnimation();

  return (
    <section
      ref={ref}
      className="max-w-4xl mx-auto px-6 py-20"
      data-testid="positioning-section"
    >
      <div className="flex flex-col md:flex-row items-center gap-8 md:gap-12">
        {/* Photo */}
        <div
          className={`flex-shrink-0 ${isInView ? 'animate-fade-in-up' : 'opacity-0'}`}
          style={{ animationDelay: '0.1s' }}
        >
          <img
            src="/images/brandon.jpg"
            alt="Brandon Crenshaw"
            width="192"
            height="192"
            loading="lazy"
            className="w-32 h-32 md:w-48 md:h-48 rounded-2xl object-cover shadow-lg"
            data-testid="quote-photo"
          />
        </div>

        {/* Statement */}
        <div className="text-center md:text-left">
          <p
            className={`text-xl md:text-2xl lg:text-3xl text-[#051A24] leading-relaxed mb-6 text-balance ${isInView ? 'animate-fade-in-up' : 'opacity-0'}`}
            style={{ animationDelay: '0.2s' }}
            data-testid="positioning-statement"
          >
            I work as a technical co-founder and growth strategist in{' '}
            <span style={{ fontFamily: "'Playfair Display', serif" }} className="italic">
              one seat
            </span>
            .
          </p>

          <p
            className={`text-[#273C46] leading-relaxed ${isInView ? 'animate-fade-in-up' : 'opacity-0'}`}
            style={{ animationDelay: '0.3s' }}
            data-testid="positioning-detail"
          >
            Most teams need both and hire neither well. I write the code, pick the pricing, and sit on
            the sales calls, so the product and the story it tells never drift apart.
          </p>
        </div>
      </div>
    </section>
  );
};

// Pricing Section
const PricingSection = ({ onOpenContact }) => {
  const { ref, isInView } = useInViewAnimation();

  return (
    <section
      ref={ref}
      id="pricing"
      className="max-w-7xl mx-auto px-6 py-20 scroll-mt-24"
      data-testid="pricing-section"
    >
      <div className="max-w-[1200px] mx-auto mb-12">
        <p
          className={`font-mono text-xs uppercase tracking-[0.2em] text-[#273C46]/70 mb-3 ${isInView ? 'animate-fade-in-up' : 'opacity-0'}`}
        >
          Working together
        </p>
        <h2
          className={`text-3xl md:text-4xl text-[#051A24] ${isInView ? 'animate-fade-in-up' : 'opacity-0'}`}
          style={{ animationDelay: '0.1s' }}
        >
          Two ways to{' '}
          <span style={{ fontFamily: "'Playfair Display', serif" }} className="italic">
            start
          </span>
        </h2>
      </div>

      <div className="flex flex-col lg:flex-row gap-8 justify-center">
        {/* Monthly Partnership Card - Dark */}
        <div
          className={`bg-[#051A24] text-white rounded-3xl p-8 lg:p-10 w-full lg:w-[400px] ${isInView ? 'animate-fade-in-up' : 'opacity-0'}`}
          style={{ animationDelay: '0.1s' }}
          data-testid="pricing-card-monthly"
        >
          <div className="mb-6">
            <span className="text-xs font-mono text-[#E0EBF0]/70 uppercase tracking-wider">
              Partnership
            </span>
            <h3 className="text-2xl font-medium mt-2">Monthly Partnership</h3>
          </div>

          <div className="mb-6">
            <span className="text-4xl font-medium">$5,000</span>
            <span className="text-[#E0EBF0]/70">/month</span>
          </div>

          <ul className="space-y-3 mb-8 text-sm text-[#E0EBF0]/90">
            <li className="flex items-start gap-2">
              <span className="text-[#E0EBF0]">•</span>
              AI product development, shipped weekly
            </li>
            <li className="flex items-start gap-2">
              <span className="text-[#E0EBF0]">•</span>
              Agents and workflows that run your operations
            </li>
            <li className="flex items-start gap-2">
              <span className="text-[#E0EBF0]">•</span>
              Go-to-market strategy and the systems behind it
            </li>
            <li className="flex items-start gap-2">
              <span className="text-[#E0EBF0]">•</span>
              Unlimited async communication
            </li>
          </ul>

          <Button variant="secondary" className="w-full" onClick={onOpenContact} data-testid="pricing-monthly-btn">
            Get started
          </Button>
        </div>

        {/* Custom Project Card - White */}
        <div
          className={`bg-white border border-[#E0EBF0] rounded-3xl p-8 lg:p-10 w-full lg:w-[400px] ${isInView ? 'animate-fade-in-up' : 'opacity-0'}`}
          style={{ animationDelay: '0.2s' }}
          data-testid="pricing-card-custom"
        >
          <div className="mb-6">
            <span className="text-xs font-mono text-[#273C46]/70 uppercase tracking-wider">
              Project-Based
            </span>
            <h3 className="text-2xl font-medium text-[#051A24] mt-2">Custom Project</h3>
          </div>

          <div className="mb-6">
            <span className="text-4xl font-medium text-[#051A24]">$25,000</span>
            <span className="text-[#273C46]/70">+</span>
          </div>

          <ul className="space-y-3 mb-8 text-sm text-[#273C46]">
            <li className="flex items-start gap-2">
              <span className="text-[#051A24]">•</span>
              A product, or an operation that runs itself
            </li>
            <li className="flex items-start gap-2">
              <span className="text-[#051A24]">•</span>
              Fixed timeline &amp; milestones
            </li>
            <li className="flex items-start gap-2">
              <span className="text-[#051A24]">•</span>
              Full source code ownership
            </li>
            <li className="flex items-start gap-2">
              <span className="text-[#051A24]">•</span>
              Post-launch support included
            </li>
          </ul>

          <Button variant="primary" className="w-full" onClick={onOpenContact} data-testid="pricing-custom-btn">
            Discuss project
          </Button>
        </div>
      </div>
    </section>
  );
};

// Testimonial Card
const TestimonialCard = ({ testimonial, opacity, style }) => (
  <div
    className="transition-opacity duration-700 ease-in-out"
    style={{ opacity, ...style }}
  >
    <div className="bg-white border border-[#E0EBF0] rounded-2xl p-8 flex flex-col" style={{ height: '320px' }}>
      <p className="text-lg md:text-xl text-[#051A24] mb-6 leading-relaxed flex-1 overflow-hidden">
        "{testimonial.quote}"
      </p>
      <div className="flex items-center gap-4 mt-auto pt-4 border-t border-[#E0EBF0]/50">
        <div className="w-12 h-12 rounded-full bg-[#051A24] flex items-center justify-center text-white font-semibold text-lg flex-shrink-0 overflow-hidden">
          {testimonial.avatar ? (
            <img
              src={testimonial.avatar}
              alt={testimonial.author}
              loading="lazy"
              className="w-full h-full object-cover"
              onError={(e) => { e.target.style.display = 'none'; e.target.nextSibling.style.display = 'flex'; }}
            />
          ) : null}
          <span className="flex items-center justify-center w-full h-full" style={{ display: testimonial.avatar ? 'none' : 'flex' }}>
            {testimonial.author.split(' ').map(n => n[0]).join('').slice(0, 2)}
          </span>
        </div>
        <div>
          <p className="font-medium text-[#051A24]">{testimonial.author}</p>
          <p className="text-sm text-[#273C46]">{testimonial.role}</p>
        </div>
      </div>
    </div>
  </div>
);

// Testimonial Carousel
const TestimonialCarousel = () => {
  const { ref, isInView } = useInViewAnimation();
  const [currentIndex, setCurrentIndex] = useState(0);
  const intervalRef = useRef(null);

  const startAutoPlay = useCallback(() => {
    // Respect a reduced-motion preference: no auto-advance, arrows still work.
    if (window.matchMedia?.('(prefers-reduced-motion: reduce)').matches) return;
    if (intervalRef.current) clearInterval(intervalRef.current);
    intervalRef.current = setInterval(() => {
      setCurrentIndex((prev) => (prev + 1) % testimonials.length);
    }, 6000);
  }, []);

  const stopAutoPlay = useCallback(() => {
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
      intervalRef.current = null;
    }
  }, []);

  useEffect(() => {
    startAutoPlay();
    return () => stopAutoPlay();
  }, [startAutoPlay, stopAutoPlay]);

  const goToPrev = () => {
    setCurrentIndex((prev) => (prev - 1 + testimonials.length) % testimonials.length);
    stopAutoPlay();
    startAutoPlay();
  };

  const goToNext = () => {
    setCurrentIndex((prev) => (prev + 1) % testimonials.length);
    stopAutoPlay();
    startAutoPlay();
  };

  const nextIndex = (currentIndex + 1) % testimonials.length;

  return (
    <section
      ref={ref}
      className="max-w-7xl mx-auto px-6 py-20"
      data-testid="testimonial-carousel-section"
    >
      <div className="mb-12">
        <p
          className={`font-mono text-xs uppercase tracking-[0.2em] text-[#273C46]/70 mb-3 ${isInView ? 'animate-fade-in-up' : 'opacity-0'}`}
        >
          References
        </p>
        <h2
          className={`text-3xl md:text-4xl text-[#051A24] ${isInView ? 'animate-fade-in-up' : 'opacity-0'}`}
          style={{ animationDelay: '0.1s' }}
          data-testid="testimonials-heading"
        >
          What{' '}
          <span style={{ fontFamily: "'Playfair Display', serif" }} className="italic">
            clients
          </span>{' '}
          say
        </h2>
      </div>

      {/* Cards */}
      <div
        className={`${isInView ? 'animate-fade-in-up' : 'opacity-0'}`}
        style={{ animationDelay: '0.2s' }}
      >
        {/* Mobile: single card */}
        <div className="md:hidden" data-testid={`testimonial-card-${currentIndex}`}>
          <TestimonialCard testimonial={testimonials[currentIndex]} opacity={1} />
        </div>

        {/* Desktop: two cards, second fades out */}
        <div className="hidden md:grid md:grid-cols-2 gap-6">
          <div data-testid={`testimonial-card-${currentIndex}`}>
            <TestimonialCard testimonial={testimonials[currentIndex]} opacity={1} />
          </div>
          <div
            className="relative"
            data-testid={`testimonial-card-${nextIndex}`}
          >
            {/* Fade-out gradient overlay on the right card */}
            <div className="absolute inset-0 z-10 pointer-events-none bg-gradient-to-l from-white via-white/60 to-transparent rounded-2xl" />
            <TestimonialCard testimonial={testimonials[nextIndex]} opacity={0.6} />
          </div>
        </div>
      </div>

      {/* Navigation + dots */}
      <div
        className={`flex items-center gap-4 mt-8 ${isInView ? 'animate-fade-in-up' : 'opacity-0'}`}
        style={{ animationDelay: '0.3s' }}
      >
        <button
          onClick={goToPrev}
          className="p-3 rounded-full border border-[#E0EBF0] hover:bg-[#E0EBF0] transition-colors"
          data-testid="carousel-prev-btn"
          aria-label="Previous testimonial"
        >
          <ChevronLeft className="w-5 h-5 text-[#051A24]" />
        </button>
        <button
          onClick={goToNext}
          className="p-3 rounded-full border border-[#E0EBF0] hover:bg-[#E0EBF0] transition-colors"
          data-testid="carousel-next-btn"
          aria-label="Next testimonial"
        >
          <ChevronRight className="w-5 h-5 text-[#051A24]" />
        </button>
        <div className="flex gap-1 ml-2">
          {testimonials.map((_, i) => (
            <button
              key={i}
              onClick={() => { setCurrentIndex(i); stopAutoPlay(); startAutoPlay(); }}
              className="w-11 h-11 flex items-center justify-center"
              aria-label={`Go to testimonial ${i + 1}`}
              aria-current={i === currentIndex}
            >
              <span
                className={`block h-2 rounded-full transition-all duration-300 ${
                  i === currentIndex ? 'bg-[#051A24] w-6' : 'bg-[#E0EBF0] hover:bg-[#273C46]/30 w-2'
                }`}
              />
            </button>
          ))}
        </div>
      </div>
    </section>
  );
};

// Closing CTA
const PartnerCTASection = ({ onOpenContact }) => {
  const { ref, isInView } = useInViewAnimation();

  return (
    <section
      ref={ref}
      id="contact"
      className="max-w-7xl mx-auto px-6 py-20 scroll-mt-24"
      data-testid="partner-cta-section"
    >
      <div
        className={`relative bg-[#051A24] rounded-3xl p-12 md:p-20 text-center overflow-hidden ${isInView ? 'animate-fade-in-up' : 'opacity-0'}`}
        style={{ animationDelay: '0.1s', minHeight: '360px' }}
        data-testid="partner-cta-container"
      >
        <div className="relative z-10 flex flex-col items-center justify-center h-full">
          <h2 className="text-3xl md:text-4xl lg:text-5xl text-white mb-6 text-balance">
            Tell me what you're{' '}
            <span style={{ fontFamily: "'Playfair Display', serif" }} className="italic">
              building
            </span>
            .
          </h2>
          <p className="text-[#E0EBF0]/90 max-w-xl mx-auto mb-8">
            I'll come back with the shortest path to shipping it, and whether I'm the right person to help.
          </p>
          <Button
            variant="secondary"
            onClick={onOpenContact}
            data-testid="partner-cta-btn"
          >
            Book a 20 minute call <ArrowRight className="w-4 h-4" />
          </Button>
        </div>
      </div>
    </section>
  );
};

// Footer
const Footer = () => {
  const { ref, isInView } = useInViewAnimation();

  const footerLinks = [
    { label: 'X', href: 'https://x.com/brandononchain' },
    { label: 'LinkedIn', href: 'https://www.linkedin.com/in/brandononchain' },
    { label: 'GitHub', href: 'https://github.com/brandononchain' },
    { label: 'Email', href: 'mailto:me@brandononchain.com' },
  ];

  return (
    <footer
      ref={ref}
      className="border-t border-[#E0EBF0]"
      data-testid="footer"
    >
      <div className="max-w-7xl mx-auto px-6 py-12">
        {/* Links */}
        <div
          className={`flex flex-wrap justify-center gap-6 mb-8 ${isInView ? 'animate-fade-in-up' : 'opacity-0'}`}
          style={{ animationDelay: '0.1s' }}
        >
          {footerLinks.map((link, index) => (
            <a
              key={index}
              href={link.href}
              className="text-[#273C46] hover:text-[#051A24] transition-colors text-sm min-w-[44px] min-h-[44px] inline-flex items-center justify-center px-3"
              data-testid={`footer-link-${link.label.toLowerCase()}`}
            >
              {link.label}
            </a>
          ))}
        </div>

        {/* Copyright */}
        <div
          className={`text-center ${isInView ? 'animate-fade-in-up' : 'opacity-0'}`}
          style={{ animationDelay: '0.2s' }}
        >
          <p className="text-[#273C46]/80 text-sm">
            © {new Date().getFullYear()} Brandon Crenshaw. All rights reserved.
          </p>
        </div>
      </div>
    </footer>
  );
};

// ============ MAIN APP ============
function App() {
  const [isContactOpen, setIsContactOpen] = useState(false);

  // URL-based routing: /work, /case-studies, /case-studies/slug, /blog, /blog/slug, or /
  const getPageFromURL = () => {
    const path = window.location.pathname.replace(/\/+$/, ''); // strip trailing slashes
    if (path.startsWith('/blog/')) {
      const slug = decodeURIComponent(path.replace('/blog/', ''));
      return { page: 'blog', slug };
    }
    if (path === '/blog') return { page: 'blog', slug: null };
    if (path === '/automations') return { page: 'automations', slug: null };
    if (path === '/case-studies') return { page: 'case-studies', slug: null };
    if (path.startsWith('/case-studies/')) {
      const slug = decodeURIComponent(path.replace('/case-studies/', ''));
      return { page: 'case-study', slug };
    }
    // Old addresses. The server redirects these; this covers a stale client.
    if (path.startsWith('/work/')) {
      const slug = decodeURIComponent(path.replace('/work/', ''));
      return { page: 'case-study', slug };
    }
    if (path === '/work') return { page: 'work', slug: null };
    return { page: 'home', slug: null };
  };

  const [route, setRoute] = useState(() => getPageFromURL());

  // Listen for browser back/forward
  useEffect(() => {
    const handlePopState = () => setRoute(getPageFromURL());
    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, []);

  const openContact = () => setIsContactOpen(true);
  const closeContact = () => setIsContactOpen(false);

  const navigateBlog = (slug) => {
    const path = slug ? `/blog/${slug}` : '/blog';
    window.history.pushState({}, '', path);
    setRoute({ page: 'blog', slug: slug || null });
    window.scrollTo(0, 0);
  };

  const navigateWork = () => {
    window.history.pushState({}, '', '/work');
    setRoute({ page: 'work', slug: null });
    window.scrollTo(0, 0);
  };

  const navigateCaseStudy = (slug) => {
    window.history.pushState({}, '', `/case-studies/${slug}`);
    setRoute({ page: 'case-study', slug });
    window.scrollTo(0, 0);
  };

  const navigateAutomations = () => {
    window.history.pushState({}, '', '/automations');
    setRoute({ page: 'automations', slug: null });
    window.scrollTo(0, 0);
  };

  const navigateCaseStudies = () => {
    window.history.pushState({}, '', '/case-studies');
    setRoute({ page: 'case-studies', slug: null });
    window.scrollTo(0, 0);
  };

  const navigateHome = () => {
    window.history.pushState({}, '', '/');
    setRoute({ page: 'home', slug: null });
    window.scrollTo(0, 0);
  };

  const scrollToWork = () => {
    document.getElementById('work')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
  };

  const navProps = {
    onOpenContact: openContact,
    onNavigateHome: navigateHome,
    onNavigateBlog: () => navigateBlog(),
    onNavigateWork: navigateWork,
    onNavigateCaseStudies: navigateCaseStudies,
    onNavigateAutomations: navigateAutomations,
  };

  if (route.page === 'blog') {
    return (
      <div data-testid="app-container">
        <SiteNav {...navProps} variant="blog" />
        <BlogPage onNavigateHome={navigateHome} initialSlug={route.slug} onSlugChange={(slug) => {
          const path = slug ? `/blog/${slug}` : '/blog';
          window.history.pushState({}, '', path);
          setRoute({ page: 'blog', slug });
        }} />
        <ContactModal isOpen={isContactOpen} onClose={closeContact} />
      </div>
    );
  }

  if (route.page === 'work') {
    return (
      <div className="min-h-screen bg-white" data-testid="app-container">
        <SiteNav {...navProps} variant="work" />
        <WorkPage
          onNavigateHome={navigateHome}
          onOpenContact={openContact}
          onNavigateCaseStudy={navigateCaseStudy}
          onNavigateCaseStudies={navigateCaseStudies}
          onNavigateAutomations={navigateAutomations}
        />
        <Footer />
        <ContactModal isOpen={isContactOpen} onClose={closeContact} />
      </div>
    );
  }

  if (route.page === 'automations') {
    return (
      <div className="min-h-screen bg-white" data-testid="app-container">
        <SiteNav {...navProps} variant="automations" />
        <AutomationsPage
          onNavigateHome={navigateHome}
          onOpenContact={openContact}
          onNavigateCaseStudy={navigateCaseStudy}
        />
        <Footer />
        <ContactModal isOpen={isContactOpen} onClose={closeContact} />
      </div>
    );
  }

  if (route.page === 'case-studies') {
    return (
      <div className="min-h-screen bg-white" data-testid="app-container">
        <SiteNav {...navProps} variant="case-studies" />
        <CaseStudiesPage
          onNavigateHome={navigateHome}
          onNavigateWork={navigateWork}
          onNavigateCaseStudy={navigateCaseStudy}
        />
        <Footer />
        <ContactModal isOpen={isContactOpen} onClose={closeContact} />
      </div>
    );
  }

  if (route.page === 'case-study') {
    return (
      <div className="min-h-screen bg-white" data-testid="app-container">
        <SiteNav {...navProps} variant="case-studies" />
        <Suspense fallback={<div className="min-h-[60vh]" aria-busy="true" />}>
          <CaseStudyPage
            slug={route.slug}
            onNavigateWork={navigateCaseStudies}
            onNavigateCaseStudy={navigateCaseStudy}
            onOpenContact={openContact}
          />
        </Suspense>
        <Footer />
        <ContactModal isOpen={isContactOpen} onClose={closeContact} />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-white" data-testid="app-container">
      <SiteNav {...navProps} variant="home" />
      <HeroSection onOpenContact={openContact} onScrollToWork={scrollToWork} />
      <SelectedWorkSection
        onNavigateWork={navigateWork}
        onNavigateCaseStudy={navigateCaseStudy}
        onNavigateCaseStudies={navigateCaseStudies}
      />
      <PositioningSection />
      <HowIWorkSection />
      <TestimonialCarousel />
      <PricingSection onOpenContact={openContact} />
      <FAQ />
      <OpenSourceSection />
      <PartnerCTASection onOpenContact={openContact} />
      <Footer />
      <ContactModal isOpen={isContactOpen} onClose={closeContact} />
    </div>
  );
}

export default App;
